import { FC, useState } from 'react';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import { EditTableIcon, ExportIcon, NavCloseIcon } from 'assets/icons';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { JobListItem } from '../jobs.model';
import RescheduleDropdown, { RescheduleOption } from './RescheduleDropdown';
import ChangeStatusDropdown from './ChangeStatusDropdown';
import PushByCustomTimeModal from './PushByCustomTimeModal';
import EditScheduleModal from './EditScheduleModal';
import ReassignJobModal from './ReassignJobModal';
import { scheduleLaterToday6PM, scheduleNextWeekMonday9AM, scheduleTomorrow9AM } from '../utils';
import type { ScheduleTime } from '../constant';
import { JobStatus, PushScheduleBy } from '../constant';

interface JobsTableActionsProps {
  selectedJobs: JobListItem[];
  onClearSelection: () => void;
  onExportClick: () => void;
  onEditTableClick: () => void;
  onModifyTagsClick: () => void;
  onRescheduleJobs: (params: {
    scheduledFrom?: string;
    scheduledTo?: string;
    pushScheduleBy?: PushScheduleBy;
    pushScheduleByValue?: number;
  }) => Promise<boolean>;
  onChangeJobStatus: (status: JobStatus) => Promise<boolean>;
  onReassignJobs: (assignees: string[], serviceAreaId?: string) => Promise<boolean>;
  isActionUpdating: boolean;
  totalJobsCount: number;
  isSelectAllAcrossPagination: boolean;
  onSelectAllAcrossPagination: () => void;
  showSelectAllAcrossPagination: boolean;
  effectiveSelectedCount: number;
}

const JobsTableActions: FC<JobsTableActionsProps> = ({
  selectedJobs,
  onClearSelection,
  onExportClick,
  onEditTableClick,
  onModifyTagsClick,
  onRescheduleJobs,
  onChangeJobStatus,
  onReassignJobs,
  isActionUpdating,
  totalJobsCount,
  isSelectAllAcrossPagination,
  onSelectAllAcrossPagination,
  showSelectAllAcrossPagination,
  effectiveSelectedCount
}) => {
  const [showPushByCustomTimeModal, setShowPushByCustomTimeModal] = useState(false);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [showReassignJobModal, setShowReassignJobModal] = useState(false);

  const handleStatusChange = async (status: JobStatus) => {
    await onChangeJobStatus(status);
  };

  const handleRescheduleAction = async (scheduleTime: ScheduleTime) => {
    if (onRescheduleJobs) {
      await onRescheduleJobs({
        scheduledFrom: scheduleTime.scheduledFrom,
        scheduledTo: scheduleTime.scheduledTo
      });
    }
  };

  const handlePushBySchedule = async (pushScheduleBy: PushScheduleBy, pushScheduleByValue: number) => {
    if (onRescheduleJobs) {
      await onRescheduleJobs({
        pushScheduleBy,
        pushScheduleByValue
      });
    }
  };

  const handlePushByCustomTime = async (value: number, unit: PushScheduleBy) => {
    await handlePushBySchedule(unit, value);
    setShowPushByCustomTimeModal(false);
  };

  const handleMoveToCustomDate = async (scheduledFrom: string, scheduledTo: string) => {
    if (onRescheduleJobs) {
      await onRescheduleJobs({
        scheduledFrom,
        scheduledTo
      });
    }
    setShowEditScheduleModal(false);
  };

  const handleReassignJobs = async (assignees: string[], serviceAreaId?: string) => {
    if (onReassignJobs) {
      await onReassignJobs(assignees, serviceAreaId);
    }
    setShowReassignJobModal(false);
  };

  const rescheduleOptions: RescheduleOption[] = [
    {
      id: 'push-by-1-hour',
      label: 'Push by 1 hour',
      action: async () => await handlePushBySchedule(PushScheduleBy.HOUR, 1)
    },
    {
      id: 'later-today-6pm',
      label: 'Later today (6 PM)',
      action: async () => await handleRescheduleAction(scheduleLaterToday6PM())
    },
    {
      id: 'tomorrow-9am',
      label: 'Tomorrow (9 AM)',
      action: async () => await handleRescheduleAction(scheduleTomorrow9AM())
    },
    {
      id: 'next-week-monday-9am',
      label: 'Next week (Mon 9 AM)',
      action: async () => await handleRescheduleAction(scheduleNextWeekMonday9AM())
    },
    {
      id: 'push-by-custom-time',
      label: 'Push by custom time',
      action: () => setShowPushByCustomTimeModal(true)
    },
    {
      id: 'move-to-custom-date',
      label: 'Move to custom date',
      action: () => setShowEditScheduleModal(true)
    }
  ];

  return (
    <>
      <div
        className="flex h-10 w-full items-center border-y border-borderGray
       bg-[#F4F4F4] py-1 px-[30px]">
        {selectedJobs.length > 0 || isSelectAllAcrossPagination ? (
          <div className="flex h-[32px] w-full items-center justify-between gap-4">
            <div className="flex items-center justify-start gap-4">
              <span className="text-sbase font-semibold text-accentText">
                {isSelectAllAcrossPagination
                  ? `All ${effectiveSelectedCount || totalJobsCount} jobs selected`
                  : `${selectedJobs.length} ${selectedJobs.length === 1 ? 'job' : 'jobs'} selected`}
              </span>
              <div className="flex items-center gap-6">
                {onRescheduleJobs && (
                  <RescheduleDropdown options={rescheduleOptions} className="shrink-0" isLoading={isActionUpdating} />
                )}
                {onChangeJobStatus && (
                  <ChangeStatusDropdown
                    onStatusChange={handleStatusChange}
                    className="shrink-0"
                    isLoading={isActionUpdating}
                  />
                )}
                {onReassignJobs && (
                  <CustomButton
                    id="reassign-jobs-button"
                    type={ButtonType.GHOST}
                    onClick={() => setShowReassignJobModal(true)}
                    disabled={isActionUpdating}
                    className="h-fit p-1 text-sbase font-semibold text-secondary">
                    Re-assign job
                  </CustomButton>
                )}
                {onModifyTagsClick && (
                  <CustomButton
                    id="modify-tags-button"
                    type={ButtonType.GHOST}
                    onClick={onModifyTagsClick}
                    disabled={isActionUpdating}
                    className="h-fit p-1 text-sbase font-semibold text-secondary">
                    Modify tags
                  </CustomButton>
                )}
              </div>
            </div>
            <TooltipWrapper tooltip="Clear selection" position="top">
              <div id="clear-selection" className="cursor-pointer p-1" onClick={onClearSelection}>
                <NavCloseIcon className="h-[10px] w-[10px]" />
              </div>
            </TooltipWrapper>
          </div>
        ) : (
          <div className="flex w-full items-center justify-end gap-4">
            <CustomButton
              id="export-table-button"
              aria-label="Export table"
              StartIcon={ExportIcon}
              startIconStyle="path-fill-current text-primaryText"
              type={ButtonType.SECONDARY}
              className="h-8 border border-gray-1 bg-transparent py-1.5 px-3 text-sbase font-semibold
               text-primaryText focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={onExportClick}>
              <span className="text-sbase font-medium">Export</span>
            </CustomButton>

            <CustomButton
              id="edit-table-button"
              aria-label="Edit table"
              StartIcon={EditTableIcon}
              startIconStyle="path-fill-current text-primaryText"
              type={ButtonType.SECONDARY}
              className="h-8 border border-gray-1 bg-transparent py-2 px-3 text-sbase font-semibold
               text-primaryText focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={onEditTableClick}>
              <span className="text-sbase font-medium">Edit table</span>
            </CustomButton>
          </div>
        )}
      </div>

      {/* Select all across pagination UI */}
      {showSelectAllAcrossPagination &&
        totalJobsCount &&
        totalJobsCount > selectedJobs.length &&
        onSelectAllAcrossPagination && (
          <div
            className="flex h-[34px] w-full items-center justify-center border-b border-borderGray
         bg-[#A4D7FA80] py-2 pl-[30px] pr-[32px]">
            <div className="flex items-center gap-2">
              <span className="text-sbase font-semibold text-accentText">
                All {selectedJobs.length} jobs on this page are selected.
              </span>
              <CustomButton
                id="select-all-across-pagination"
                type={ButtonType.GHOST}
                onClick={onSelectAllAcrossPagination}
                className="h-fit p-1 text-sbase font-semibold text-secondary">
                Select all {totalJobsCount} jobs
              </CustomButton>
            </div>
          </div>
        )}

      <PushByCustomTimeModal
        isOpen={showPushByCustomTimeModal}
        onClose={() => setShowPushByCustomTimeModal(false)}
        onUpdate={handlePushByCustomTime}
        isLoading={isActionUpdating}
      />

      {showEditScheduleModal && (
        <EditScheduleModal
          onClose={() => setShowEditScheduleModal(false)}
          onSave={handleMoveToCustomDate}
          isScheduled={false}
          scheduledFrom=""
          scheduledTo=""
          isLoading={isActionUpdating}
        />
      )}

      <ReassignJobModal
        isOpen={showReassignJobModal}
        onClose={() => setShowReassignJobModal(false)}
        onUpdate={handleReassignJobs}
        isLoading={isActionUpdating}
        selectedJobsCount={
          isSelectAllAcrossPagination ? effectiveSelectedCount || totalJobsCount || 0 : selectedJobs.length
        }
      />
    </>
  );
};

export default JobsTableActions;
